import React, { useState } from "react";
import { useConfigurePrimaryMutation, useGetStatusQuery, useTestConfigurationMutation } from "../../../store/api/admin/adminApiSlice";
import { AppState } from "../../../store";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { setServerConfig } from "../../../store/api/admin/reducers";
import { useNavigate } from "react-router-dom";

export const useRedundancy = () => {

    const steps = ["Configuration", "Settings", "Complete"];   
    const [openDialog, setOpenDialog] = useState(false);
    const [activateRedundancy] = useConfigurePrimaryMutation();
    const [testConfiguration] = useTestConfigurationMutation();
    const serverConfig = useSelector((state: AppState) => state.admin.serverConfig);
    const dispatch = useStoreDispatch();
    const [isLoading, setIsLoading] = React.useState(false);
    const [testResult, setTestResult] = React.useState<any>(null);
    const [activeStep, setActiveStep] = React.useState(0);
    const [isRedundancyActivated, setIsRedundancyActivated] = React.useState(false);
    const [skipped, setSkipped] = React.useState(new Set<number>());
    const navigate = useNavigate();
    const { refetch: refetchStatus } = useGetStatusQuery();    

  const handleAddRedundancyClick = () => {
    setOpenDialog(true);
  };

  const handleTestConfiguration = async () => {
      setIsLoading(true);
      try {
        const result:any = await testConfiguration(serverConfig).unwrap();
        setTestResult(result);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
  
        if (result && typeof result === 'object') {
          Object.values(result).forEach(value => {
            if (typeof value === 'string' && value.startsWith("Error")) {
              dispatch(setOpenToast({ openToast: true, message: value, toastType: "error" }));
            }
          });
        }
      } catch (error) {
        console.error("Test Configuration failed:", error);
        dispatch(setOpenToast({ openToast: true, message: "Test Configuration failed", toastType: "error" }));
      } finally {
        setIsLoading(false);
      }
    };

    const handleActivateRedundancy = async () => {
        setIsLoading(true);
        try {
            await activateRedundancy(serverConfig).unwrap();
            setIsRedundancyActivated(true);
            setOpenDialog(false);
            const refetchResult = await refetchStatus();
            dispatch(
                setOpenToast({
                    openToast: true,
                    toastType: "success",
                    message: "Redundancy activated successfully"
                })
            );
        } catch (error) {
            console.error("Error in handleActivateRedundancy:", error);
            dispatch(
                setOpenToast({
                    openToast: true,
                    toastType: "error",
                    message: (error as { data?: string })?.data || "Failed to activate redundancy"
                })
            );
        } finally {
            setIsLoading(false);
        }
    }

      const getButtonText = () => {
        if (activeStep === steps.length - 1) {
          return "Activate Redundancy";
        } else if (activeStep === 1) {
          return "Test Configuration";
        } else {
          return "Next";
        }
      };


  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    if (activeStep === 1) {
      handleTestConfiguration();
    } else {
      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }

      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped(newSkipped);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const resetStepper = () => {
    setActiveStep(0);
    setTestResult(null);
    setIsLoading(false);
    setSkipped(new Set<number>());
    dispatch(setServerConfig({
      primary_address: '',
      secondary_address: '',
      observer_address: '',
      heartbeat_freqency: 0,
      missed_heartbeat_threshold: 0,
      secondary_username: '',
      secondary_password: '',
    }));
  };
    

  return {
    openDialog,
    setOpenDialog,
    handleAddRedundancyClick,
    activateRedundancy,
    testConfiguration,
    serverConfig,
    handleTestConfiguration,
    handleActivateRedundancy,
    isLoading,
    testResult,
    steps,
    getButtonText,
    handleBack,
    handleNext,
    activeStep,
    isStepSkipped,
    resetStepper
  }
}